import axios from 'axios'
import { useEffect, useState } from 'react'
import { getUserIdentity } from '../services/api/auth'
import { clearSavedTokens, getAccessToken } from '../services/authentication'
import { environmentVariables } from '../utils/environmentVariables'
import { redirectToUrl } from '../utils/location'
import { useCurrentUser } from './useCurrentUser'

interface UseUserPhoneNumberResult {
  phoneNumber: string | null
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export const useUserPhoneNumber = (): UseUserPhoneNumberResult => {
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const currentUser = useCurrentUser()

  const fetchPhoneNumber = async () => {
    if (!currentUser?.uuid) {
      setPhoneNumber(null)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const accessToken = getAccessToken()
      if (!accessToken) {
        clearSavedTokens()
        redirectToUrl(`${environmentVariables('AUTH_API_URL')}/signin`)
        return
      }

      const identity = await getUserIdentity(currentUser.uuid, accessToken)
      setPhoneNumber(identity.phone_number || null)
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        clearSavedTokens()
        redirectToUrl(`${environmentVariables('AUTH_API_URL')}/signin`)
        return
      }
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch phone number'
      setError(errorMessage)
      setPhoneNumber(null)
    } finally {
      setIsLoading(false)
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    fetchPhoneNumber()
  }, [currentUser?.uuid])

  return {
    phoneNumber,
    isLoading,
    error,
    refetch: fetchPhoneNumber,
  }
}
